import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, LinearProgress } from '@mafc/trolly';

import { INewCartEntry } from 'pages/NewCart/ICart';
import extractQuantityProps from 'pages/Cart/components/UpdateQuantity/helpers/extractQuantityProps.helper';
import { reformProductURL } from 'pages/NewCart/helpers/cartListItem.helper';
import { isExpressShipment, isMKPShipment } from 'pages/NewCart/helpers/shipment.helper';
import QuantityUpdate from '../QuantityUpdate';
import { isNonEmptyArray } from 'utils/helpers.util';
import { createMarkup } from 'utils/html.util';

import ExtendedWarranty from 'common/ExtendedWarrantyC4New';
import Prices from '../Prices';
import useOOSItem from 'pages/NewCart/hooks/useOOSItem';
import ProductImage from '../ProductImage';

import * as S from './CartListItem.style';
import { Marquee, BundleText } from '@mafc/common';

const CartListItem: React.FC<{
  entry: INewCartEntry;
  isLoading: boolean;
  shipmentType: string;
  isOOSSubstitutionDrawer?: boolean;
  showQtySelector?: boolean;
  variant?: string;
  isCampaign?: boolean;
  onSubstituteQtyChange?: (qty: number) => void;
}> = ({
  entry = {} as INewCartEntry,
  shipmentType,
  isLoading,
  isOOSSubstitutionDrawer = false,
  showQtySelector = true,
  variant = S.CartListItemVariant.Default,
  onSubstituteQtyChange,
  isCampaign,
}) => {
  const { t } = useTranslation();
  const { deleteHandler } = useOOSItem(entry);

  const {
    product,
    offerId,
    offer,
    totalPrice,
    quantity,
    serviceEntries,
    entryNumber,
    intent,
    outOfStock,
    isLoyaltyPrice = false,
    bundleId,
  } = entry;
  const { serviceProducts = [] } = product;
  const { url, mainOffer: { code = '' } = {}, code: productId, name } = product;

  const isOffer = isMKPShipment(shipmentType);
  const isNowShipment = isExpressShipment(shipmentType);
  const propsToUpdateQuantity = extractQuantityProps(entry, code);
  const productURL = reformProductURL(url, offerId);
  const isSubstitute =
    variant === S.CartListItemVariant.DrawerItem || variant === S.CartListItemVariant.DrawerHeader;
  const linkProps = productURL && !isSubstitute ? { href: productURL, as: 'a' } : { as: 'p' };

  return (
    <S.CartListItem
      variant={variant}
      isExpressProduct={product.isExpressProduct}
      isCampaign={isCampaign}
    >
      <S.Product>
        <ProductImage
          product={product}
          mr={'2'}
          outOfStock={outOfStock}
          isNowShipment={isNowShipment}
        />
        <S.ProductDetails>
          <S.CartListItemNameLink
            dangerouslySetInnerHTML={createMarkup(name)}
            data-test="__cart_item_name_link__"
            {...linkProps}
          />
          {entry.bundleText && (
            <Flex>
              <BundleText>
                {entry.bundleText.length > 10 ? (
                  <Marquee text={entry.bundleText} />
                ) : (
                  entry.bundleText
                )}
              </BundleText>
            </Flex>
          )}
          {isLoyaltyPrice && <S.SharePriceBadge>{t('cartShipment.sharePrice')}</S.SharePriceBadge>}
          {outOfStock && !isOOSSubstitutionDrawer ? (
            <S.OOSItemRow>
              <S.RemoveOOSItemBtn variant="custom" onClick={deleteHandler}>
                <>{t('remove')}</>
              </S.RemoveOOSItemBtn>
            </S.OOSItemRow>
          ) : (
            <S.PriceAndQuantityRow>
              <Prices offer={offer} totalPrice={totalPrice} isLoyaltyPrice={isLoyaltyPrice} />

              {propsToUpdateQuantity && showQtySelector && !outOfStock && (
                <QuantityUpdate
                  {...propsToUpdateQuantity}
                  isOffer={isOffer}
                  offerId={offerId}
                  productId={productId}
                  intent={intent}
                  isSubstitute={isSubstitute}
                  bundleId={bundleId}
                  onSubstituteQtyChange={onSubstituteQtyChange}
                />
              )}
            </S.PriceAndQuantityRow>
          )}
        </S.ProductDetails>
      </S.Product>

      {isNonEmptyArray(serviceProducts) ? (
        <S.ExtendedWarranty>
          <ExtendedWarranty
            productEntryNumber={entryNumber}
            pid={productId}
            serviceProducts={serviceProducts}
            serviceEntries={serviceEntries}
            quantity={quantity}
            intent={intent || ''}
            offerId={offerId || ''}
          />
        </S.ExtendedWarranty>
      ) : null}

      {isLoading && <LinearProgress position="bottom" />}
    </S.CartListItem>
  );
};

export default CartListItem;
